import React, { useState } from 'react';
import { Search, X, ChevronDown, ChevronUp } from 'lucide-react';

function SearchBar({ filters, onFilterChange, onClearFilters, isAdmin }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const handleInputChange = (field, value) => {
    onFilterChange({ ...filters, [field]: value });
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');
  const hasAdvancedFilters = filters.grower || filters.submittedBy || filters.dateFrom || filters.dateTo || filters.minSamples;

  return (
    <div className="bg-white rounded-lg shadow mb-4">
      {/* Compact Header with Quick Filters */}
      <div className="p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Search size={18} className="text-gray-400 flex-shrink-0" />
          
          {/* Quick Search: Batch ID */}
          <input
            type="text"
            placeholder="Search Batch ID..."
            value={filters.batchId}
            onChange={(e) => handleInputChange('batchId', e.target.value)}
            className="flex-1 min-w-[200px] px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />

          {/* Quick Filter: Company (Admin) or Grower (Non-Admin) */}
          {isAdmin ? (
            <input
              type="text"
              placeholder="Company..."
              value={filters.company}
              onChange={(e) => handleInputChange('company', e.target.value)}
              className="flex-1 min-w-[150px] px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          ) : (
            <input
              type="text"
              placeholder="Grower..."
              value={filters.grower}
              onChange={(e) => handleInputChange('grower', e.target.value)}
              className="flex-1 min-w-[150px] px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          )}

          {/* Quick Filter: Status */}
          <select
            value={filters.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Sent to Lab">Sent to Lab</option>
            <option value="Results Received">Results Received</option>
            <option value="Completed">Completed</option>
          </select>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 ml-auto">
            {hasActiveFilters && (
              <button
                onClick={onClearFilters}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100"
              >
                <X size={14} />
                Clear
              </button>
            )}
            
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 px-2 py-1 rounded hover:bg-blue-50 font-medium"
            >
              {isExpanded ? (
                <>
                  <ChevronUp size={14} />
                  Less
                </>
              ) : (
                <>
                  <ChevronDown size={14} />
                  More Filters
                  {hasAdvancedFilters && (
                    <span className="ml-1 bg-blue-100 text-blue-700 px-1.5 rounded-full text-xs">•</span>
                  )}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Advanced Filters - Collapsible */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Search by Grower */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Grower
              </label>
              <input
                type="text"
                placeholder="Grower name"
                value={filters.grower}
                onChange={(e) => handleInputChange('grower', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              />
            </div>

            {/* Search by Submitted By */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Submitted By
              </label>
              <input
                type="text"
                placeholder="Rep name"
                value={filters.submittedBy}
                onChange={(e) => handleInputChange('submittedBy', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              />
            </div>

            {/* Date From */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Date From
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleInputChange('dateFrom', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Date To
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleInputChange('dateTo', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              />
            </div>

            {/* Sample Count Range */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Min Samples
              </label>
              <input
                type="number"
                placeholder="0"
                min="0"
                value={filters.minSamples}
                onChange={(e) => handleInputChange('minSamples', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchBar;
